import {
  CheckBadgeIcon,
  CheckCircleIcon,
  HandRaisedIcon,
  ShieldCheckIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { Modal } from '@retainerclub/common';
import PropTypes from 'prop-types';
import { useState } from 'react';

const confirmationItems = [
  {
    id: 'dental-work',
    icon: CheckBadgeIcon,
    content: (
      <>
        I confirm that I have <span className="font-semibold">NOT</span> had
        dental work (fillings, crowns, etc.) since my last scan
      </>
    ),
  },
  {
    id: 'new-scan',
    icon: HandRaisedIcon,
    content: (
      <>
        I understand that if the retainers don't fit, I may need to see my
        practice for a <span className="font-semibold">new scan</span> and I am
        responsible for any fees
      </>
    ),
  },
  {
    id: 'guarantee',
    icon: ShieldCheckIcon,
    content: (
      <>
        Your 15 Day -{' '}
        <span className="font-semibold">Perfect Fit Guarantee</span> begins the
        day your retainers are delivered. Try them on immediately to verify fit
      </>
    ),
  },
  {
    id: 'shipping-address',
    icon: TruckIcon,
    content: <>I have verified that the shipping address is correct</>,
  },
];

function CheckoutFitConfirmationModal({
  open,
  onBack,
  onPlaceOrder,
  isSubmitting = false,
}) {
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  function handleBack() {
    setIsAcknowledged(false);
    onBack();
  }

  function handlePlaceOrder() {
    if (!isAcknowledged || isSubmitting) {
      return;
    }

    onPlaceOrder();
  }

  return (
    <Modal
      open={open}
      setOpen={handleBack}
      size="3xl"
      showCloseButton={false}
      panelClass="relative w-full overflow-hidden rounded-lg bg-white px-5 py-7 text-left shadow-xl transition duration-300 ease-out data-closed:translate-y-4 data-enter:translate-y-0 data-leave:translate-y-4 data-closed:opacity-0 data-enter:opacity-100 data-leave:opacity-0 sm:my-8 sm:px-8 sm:py-9 sm:data-closed:translate-y-0 sm:data-leave:translate-y-0 sm:data-closed:scale-95 sm:data-enter:scale-100 sm:data-leave:scale-95"
    >
      <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
        <CheckCircleIcon className="h-12 w-12 text-rcprimary-400" />
        <h2 className="mt-4 font-semibold text-2xl text-gray-950 sm:text-3xl">
          A Perfect Fitting Retainer is our Priority
        </h2>
        <p className="mt-3 text-gray-900 text-lg sm:text-2xl">
          Before we place your order, please verify the following:
        </p>
      </div>

      <div className="mt-8 grid gap-x-10 gap-y-7 sm:grid-cols-2 sm:gap-y-10">
        {confirmationItems.map((item) => {
          const Icon = item.icon;

          return (
            <div key={item.id} className="flex flex-col items-center">
              <Icon className="h-11 w-11 text-rcprimary-400" />
              <p className="mt-3 max-w-xs text-center text-gray-950 text-lg leading-7 sm:text-left sm:text-2xl sm:leading-9">
                {item.content}
              </p>
            </div>
          );
        })}
      </div>

      <label className="mx-auto mt-8 flex max-w-xl cursor-pointer items-start gap-x-4 text-gray-950 text-lg sm:items-center sm:text-2xl">
        <input
          type="checkbox"
          checked={isAcknowledged}
          onChange={(event) => setIsAcknowledged(event.target.checked)}
          className="mt-1 size-6 rounded-md border-gray-400 text-rcprimary-400 focus:ring-rcprimary-400 sm:mt-0 sm:size-8"
        />
        <span>I've read and understand the above</span>
      </label>

      <div className="mt-8 grid gap-3 sm:ml-auto sm:max-w-lg sm:grid-cols-2">
        <button
          type="button"
          className="inline-flex min-h-12 items-center justify-center rounded-lg border-2 border-rcprimary-400 bg-white px-6 font-medium text-gray-950 text-xl hover:bg-rcprimary-50 focus-visible:outline-2 focus-visible:outline-rcprimary-400 focus-visible:outline-offset-2"
          onClick={handleBack}
        >
          Back
        </button>
        <button
          type="button"
          className="inline-flex min-h-12 items-center justify-center rounded-lg bg-rcprimary-400 px-6 font-medium text-gray-950 text-xl hover:bg-rcprimary-300 focus-visible:outline-2 focus-visible:outline-rcprimary-400 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
          disabled={!isAcknowledged || isSubmitting}
          onClick={handlePlaceOrder}
        >
          {isSubmitting ? 'Placing Order...' : 'Place Order'}
        </button>
      </div>
    </Modal>
  );
}

CheckoutFitConfirmationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  onPlaceOrder: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
};

export default CheckoutFitConfirmationModal;
